<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 z-[9999] overflow-hidden"
    @wheel.prevent
  >
    <div class="flex items-center justify-center min-h-screen px-4 py-4">
      <!-- Overlay -->
      <div
        class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
        @click="closeModal"
      ></div>

      <!-- Modal -->
      <div
        :class="[
          'relative mx-auto w-full text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl max-h-[90vh] overflow-hidden flex flex-col',
          size === 'sm' && 'max-w-md p-6',
          size === 'md' && 'max-w-lg p-6',
          size === 'lg' && 'max-w-3xl p-6',
          size === 'xl' && 'max-w-4xl p-6',
          !size && 'max-w-md p-6',
        ]"
        ref="modalContent"
        @wheel.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between mb-4 flex-shrink-0">
          <slot name="header">
            <h3 class="text-lg font-medium text-black">{{ title }}</h3>
          </slot>
          <button
            @click="closeModal"
            class="text-black hover:text-black transition-colors"
            type="button"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Body -->
        <div
          class="mb-4 flex-1 overflow-y-auto overscroll-contain"
          ref="scrollBody"
        >
          <!-- Alert message -->
          <div
            v-if="alert"
            class="p-4 mb-4 text-sm text-blue-700 bg-blue-100 rounded-lg"
          >
            <div class="flex items-center">
              <i class="fas fa-info-circle mr-2"></i>
              {{ alert }}
            </div>
          </div>

          <!-- Content -->
          <slot name="body">
            <p v-if="content" class="text-black">{{ content }}</p>
          </slot>
        </div>

        <!-- Footer -->
        <div v-if="!hideFooter" class="flex justify-end gap-3 flex-shrink-0">
          <slot name="footer">
            <button
              @click="closeModal"
              class="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              type="button"
            >
              {{ cancelText }}
            </button>
            <button
              @click="confirmAction"
              class="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              type="button"
            >
              {{ confirmText }}
            </button>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch, ref, nextTick, onUnmounted } from "vue";

interface Props {
  title?: string;
  alert?: string;
  content?: string;
  cancelText?: string;
  confirmText?: string;
  isVisible: boolean;
  size?: "sm" | "md" | "lg" | "xl";
  hideFooter?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: "Titre par défaut",
  content: "",
  cancelText: "Annuler",
  confirmText: "Confirmer",
  hideFooter: false,
});

const emit = defineEmits<{
  "update:isVisible": [value: boolean];
  confirm: [];
}>();

const modalContent = ref<HTMLElement>();
const scrollBody = ref<HTMLElement>();

const disableBodyScroll = () => {
  document.documentElement.style.overflow = "hidden";
  document.body.style.overflow = "hidden";
};

const enableBodyScroll = () => {
  document.documentElement.style.overflow = "";
  document.body.style.overflow = "";
};

// Surveiller les changements de visibilité de la modal
watch(
  () => props.isVisible,
  (newValue) => {
    if (newValue) {
      // Attendre que le DOM soit mis à jour
      nextTick(() => {
        disableBodyScroll();
      });
    } else {
      enableBodyScroll();
    }
  },
  { immediate: true },
);

// Nettoyer lors de la destruction du composant
onUnmounted(() => {
  enableBodyScroll();
});

const closeModal = () => {
  emit("update:isVisible", false);
};

const confirmAction = () => {
  emit("confirm");
  closeModal();
};
</script>

<style scoped>
/* Animation d'entrée/sortie */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.9);
}
</style>
